import { useMemo, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { technologiesExcelUrl } from "app/pages/website-analysis/website-technologies/technologiesApi";
import { SearchResultsTableWithTrialHook } from "app/pages/sales-intelligence/common-components/search-results-table";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { TechnologiesCategoryFilter } from "app/pages/sales-intelligence/modules/technology/components/TechnologiesCategoryFilter/TechnologiesCategoryFilter";
import type { CompanyAnalysisTechnologyItem } from "app/pages/sales-intelligence/modules/technology/types/company-analysis-technology-filter";
import { getNoData } from "app/pages/sales-intelligence/pages/company-analysis/constants";
import { SubDomainFilter } from "app/pages/sales-intelligence/pages/company-analysis/containers/technologies/SubDomainFilter/SubDomainFilter";
import { getTechnographicsColumns } from "app/pages/sales-intelligence/pages/company-analysis/containers/technologies/TechnologiesContent/columns";
import { SearchTechnology } from "app/pages/website-analysis/website-technologies/filters/SearchTechnology";
import { StatusFilter } from "app/pages/website-analysis/website-technologies/filters/StatusFilter";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { TECHNOLOGIES_TRIAL_HOOK_PROPS } from "./constants/technologiesTrialHookProps";
import { StyledSearchTechnologyWrapper, TechnologiesTableWrapper } from "./styles";
import { TechnologiesFiltersLoader } from "./TechnologiesFiltersLoader";
import { useFilteredTechnologies } from "./useFilteredTechnologies";

import "./Technologies.scss";
import { EAvailabilityMode } from "app/common/services/pageClaims";

const ROW_HEIGHT = 41;

export const TechnologiesContent = ({
    technologies,
    isLoading,
    isWebsiteAnalysis = false,
}: {
    technologies: CompanyAnalysisTechnologyItem[] | undefined;
    isLoading: boolean;
    isWebsiteAnalysis?: boolean;
}) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const { translate } = useTranslate();

    const featureAvailabilityMode = isWebsiteAnalysis
        ? salesSettingsHelper.getWebsiteTechnologiesFeature()
        : salesSettingsHelper.getCompanyTechnologiesFeature();
    const isFeatureHook = featureAvailabilityMode === EAvailabilityMode.HOOK;
    const hookLimit = salesSettingsHelper.getNumTechnologiesHookLimit();

    const {
        handleApplySelectedTechCategories,
        handleSearch,
        filteredTechnologies,
        statusFilterValue,
        setStatusFilterValue,
        searchString,
        currentPage,
        setCurrentPage,
        isSubDomainFilterDisabled,
        setSelectedSubdomains,
        subdomainCounts,
        getInitiallySelectedSubdomains,
    } = useFilteredTechnologies(technologies, isFeatureHook);

    const excelUrl = useMemo(
        () =>
            isWebsiteAnalysis
                ? technologiesExcelUrl([params.key])
                : salesApiService.companyAnalysis.downloadTechnologiesExcel(params.key),
        [params.key, isWebsiteAnalysis],
    );

    const paginationProps = useMemo(
        () => ({
            pageSize: 100,
            total: filteredTechnologies.length,
            current: currentPage,
            onChange(_currentPage: number, _pageSize: number, nextPage: number) {
                setCurrentPage(nextPage);
            },
        }),
        [filteredTechnologies.length, currentPage],
    );

    const countRef = useRef(filteredTechnologies.length);
    countRef.current = filteredTechnologies.length;

    const columnConfig = useMemo(() => {
        const columns = getTechnographicsColumns(
            () => `${translate("analysis.overview.technographics.table.technology_name")} (${countRef.current})`,
        );

        if (isFeatureHook) {
            return columns.map((column) => ({
                ...column,
                sorter: false,
            }));
        }

        return columns;
    }, [isFeatureHook]);

    return (
        <>
            {isLoading ? (
                <TechnologiesFiltersLoader />
            ) : (
                <>
                    <Flex
                        padding="12px 16px"
                        alignItems="center"
                        gap="16px"
                    >
                        <TechnologiesCategoryFilter
                            technologies={technologies}
                            onDone={handleApplySelectedTechCategories}
                        />
                        {!isWebsiteAnalysis ? (
                            <SubDomainFilter
                                disabled={isSubDomainFilterDisabled}
                                onSelect={setSelectedSubdomains}
                                data={subdomainCounts}
                                onClear={() => setSelectedSubdomains(getInitiallySelectedSubdomains())}
                            />
                        ) : null}
                        <StatusFilter
                            value={statusFilterValue}
                            onChange={(value) => setStatusFilterValue(value)}
                            cssClassContainer="DropdownContent-container Popup-content--pro si-technologies-filter"
                        />
                    </Flex>
                    <StyledSearchTechnologyWrapper>
                        <SearchTechnology
                            isDisabled={isFeatureHook}
                            value={searchString}
                            onSearch={handleSearch}
                            excelUrl={excelUrl}
                        />
                    </StyledSearchTechnologyWrapper>
                </>
            )}
            <TechnologiesTableWrapper>
                <SearchResultsTableWithTrialHook
                    withArrowButtons
                    isLoading={isLoading}
                    data={filteredTechnologies}
                    columnConfig={columnConfig}
                    pagination={paginationProps}
                    noData={getNoData()}
                    isTrial={isFeatureHook}
                    trialHookProps={TECHNOLOGIES_TRIAL_HOOK_PROPS}
                    rowHeight={ROW_HEIGHT}
                    trialAvailableRows={hookLimit}
                />
            </TechnologiesTableWrapper>
        </>
    );
};
